import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { Mail } from "lucide-react";
import { currentBrand } from "@/lib/brand";
import { customerTopics } from "@/lib/help-content";
import { HelpTopicGrid } from "@/components/help/HelpTopicGrid";

export const Route = createFileRoute("/help/customers")({ component: CustomerHelpPage });

function CustomerHelpPage() {
  const { profile } = useAuth();
  const backLink = profile ? "/app/customer/dashboard" : "/";

  return (
    <div className="min-h-screen bg-background animate-in fade-in slide-in-from-bottom-4 duration-200 flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-background">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <img
              src={currentBrand.logoSrc}
              alt={`${currentBrand.name} logo`}
              className="h-8 w-auto"
            />
            <h1 className="text-lg font-semibold">FlightDeck Help</h1>
          </div>
          <Link
            to={backLink}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Back to {profile ? "Dashboard" : "FlightDeck"}
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="mx-auto max-w-4xl px-6 py-12">
          <Link
            to="/help"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8"
          >
            ← Back to help
          </Link>

          <div className="mb-12">
            <h1 className="text-4xl font-bold mb-4">Customer guide</h1>
            <p className="text-lg text-muted-foreground">
              Learn how to use FlightDeck to submit jobs, track orders, and manage your team.
            </p>
          </div>

          {profile && (
            <div className="mb-8 rounded-2xl bg-secondary/50 p-4 text-sm">
              You're signed in as{" "}
              <span className="font-medium">{profile.full_name ?? profile.email}</span>
              <Link to="/app/customer/dashboard" className="ml-4 text-primary hover:underline">
                Go to your dashboard →
              </Link>
            </div>
          )}

          <HelpTopicGrid topics={customerTopics} />

          <div className="mt-10">
            <section className="rounded-3xl border border-border bg-card p-6 shadow-soft">
              <h2 className="text-xl font-semibold mb-4">Need more help?</h2>
              <p className="text-muted-foreground mb-4">
                Contact our support team for assistance with your account or jobs.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="mailto:support@resoluteink.co.uk"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-hero px-6 py-3 text-primary-foreground shadow-soft hover:opacity-90"
                >
                  Contact support
                </a>
                <a
                  href="tel:01246202686"
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-6 py-3 hover:bg-secondary/80"
                >
                  01246 202686
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-sky-50 dark:bg-sky-950/20 py-8 border-t border-border">
        <div className="mx-auto max-w-6xl px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src={currentBrand.logoSrc}
                alt={`${currentBrand.name} logo`}
                className="h-6 w-auto"
              />
              <span className="text-sm font-medium">{currentBrand.name}</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">
                Home
              </Link>
              <Link to="/pricing" className="hover:text-foreground transition-colors">
                Pricing
              </Link>
              <a
                href="mailto:support@resoluteink.co.uk"
                className="flex items-center gap-1 hover:text-foreground transition-colors"
              >
                <Mail className="h-4 w-4" /> Support
              </a>
            </div>
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} {currentBrand.name}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
